import { useMemo } from 'react';

type FilaSeguimientoBase = {
  nroFormularioMaster: number;
  nroItemMaster: number;
  fechaEmisionComprobanteMaster?: string | null;
  codigoArticulo?: string;
};

type UseFilasSeguimientoFiltradasParams<T extends FilaSeguimientoBase> = {
  rows: T[];
  busquedaDebounced: string;
  buildSearchText: (row: T) => string;
  filtroPendiente: string;
  matchPendiente: (row: T, filtroPendiente: string) => boolean;
  fechaDesde: string;
  fechaHasta: string;
  filtroEntidadNumero: string;
  getEntidadNumero: (row: T) => number | undefined;
  codigoArticuloFiltro: string;
};

export function useFilasSeguimientoFiltradas<T extends FilaSeguimientoBase>({
  rows,
  busquedaDebounced,
  buildSearchText,
  filtroPendiente,
  matchPendiente,
  fechaDesde,
  fechaHasta,
  filtroEntidadNumero,
  getEntidadNumero,
  codigoArticuloFiltro,
}: UseFilasSeguimientoFiltradasParams<T>): T[] {
  const rowsIndexadas = useMemo(
    () =>
      rows.map((row) => ({
        row,
        searchText: buildSearchText(row).toLowerCase(),
        fechaMs: row.fechaEmisionComprobanteMaster
          ? new Date(row.fechaEmisionComprobanteMaster).getTime()
          : null,
      })),
    [rows, buildSearchText]
  );

  return useMemo(() => {
    const desdeMs = fechaDesde ? new Date(`${fechaDesde}T00:00:00`).getTime() : null;
    const hastaMs = fechaHasta ? new Date(`${fechaHasta}T23:59:59`).getTime() : null;
    const filtroEntidad = filtroEntidadNumero.trim();
    const codigoFiltro = codigoArticuloFiltro.trim().toLowerCase();
    const busqueda = busquedaDebounced.trim();

    return rowsIndexadas
      .filter(({ row, searchText, fechaMs }) => {
        if (!matchPendiente(row, filtroPendiente)) return false;

        if (desdeMs != null || hastaMs != null) {
          if (fechaMs == null) return false;
          if (desdeMs != null && fechaMs < desdeMs) return false;
          if (hastaMs != null && fechaMs > hastaMs) return false;
        }

        if (filtroEntidad && !String(getEntidadNumero(row) ?? '').includes(filtroEntidad)) {
          return false;
        }

        if (codigoFiltro && (row.codigoArticulo ?? '').trim().toLowerCase() !== codigoFiltro) {
          return false;
        }

        if (busqueda && !searchText.includes(busqueda)) {
          return false;
        }

        return true;
      })
      .map(({ row }) => row);
  }, [
    rowsIndexadas,
    busquedaDebounced,
    filtroPendiente,
    matchPendiente,
    fechaDesde,
    fechaHasta,
    filtroEntidadNumero,
    getEntidadNumero,
    codigoArticuloFiltro,
  ]);
}
